import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine, LabelList,
} from 'recharts';
import { Sparkles, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const CATEGORY_COLORS = {
  baseline:   '#2563eb',
  initiative: '#16a34a',
  synergy:    '#7c3aed',
  market:     '#d97706',
  exit:       '#0891b2',
};

const renderBarLabel = (props) => {
  const { x, y, width, value, index } = props;
  if (!value) return null;
  const item = props.chartData?.[index];
  if (!item) return null;
  const displayVal = item.isBase
    ? `$${item.value}M`
    : `${item.isPositive ? '+' : ''}$${item.isPositive ? '' : '-'}${item.value}M`;
  const color = item.isBase ? '#334155' : item.isPlanned ? '#3b82f6' : (item.isPositive ? '#16a34a' : '#dc2626');
  return (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={color}>
      {displayVal}
    </text>
  );
};

function truncate(str, max) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

function WaterfallChart({ items, portco }) {
  if (!items.length) return null;

  const baselineItems = items.filter(i => i.category === 'baseline');

  const entryEbitda = portco.entry_ebitda
    || baselineItems.reduce((sum, i) => sum + (i.underwritten_value || 0), 0)
    || 0;

  let running = entryEbitda;
  const chartData = [
    { name: 'Entry EBITDA', value: entryEbitda, base: 0, isBase: true, color: '#3b82f6' },
  ];

  items.forEach(item => {
    const hasActual = item.actual_value != null && item.actual_value !== 0;
    const delta = hasActual
      ? (item.actual_value || 0) - (item.underwritten_value || 0)
      : (item.underwritten_value || 0);

    if (delta === 0) return;
    chartData.push({
      name: truncate(item.label, 20),
      fullName: item.label,
      value: Math.abs(delta),
      base: delta >= 0 ? running : running + delta,
      isPositive: delta >= 0,
      isPlanned: !hasActual,
      color: !hasActual ? '#93c5fd' : (delta >= 0 ? '#22c55e' : '#ef4444'),
    });
    running += delta;
  });

  chartData.push({
    name: 'Projected EBITDA', value: Math.max(0, running), base: 0, isBase: true, color: '#6366f1' },
  );

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={chartData} margin={{ top: 28, right: 16, left: 16, bottom: 60 }} barCategoryGap="20%">
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: '#64748b' }}
          axisLine={{ stroke: '#e2e8f0' }}
          tickLine={false}
          angle={-35}
          textAnchor="end"
          interval={0}
          height={70}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={v => `$${v}M`}
        />
        <Tooltip
          cursor={{ fill: 'rgba(99,102,241,0.05)' }}
          formatter={(value, name, props) => {
            const item = props.payload;
            if (name === 'base') return [null, null];
            const label = item.fullName || item.name;
            if (item.isBase) return [`$${value}M`, label];
            const prefix = item.isPlanned ? '(planned) ' : '';
            return [`${prefix}${item.isPositive ? '+' : '-'}$${value}M`, label];
          }}
          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        />
        <Bar dataKey="base" stackId="a" fill="#f1f5f9" isAnimationActive={false} radius={[0, 0, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={index} fill={entry.isBase ? 'transparent' : '#f1f5f9'} />
          ))}
        </Bar>
        <Bar dataKey="value" stackId="a" radius={[4, 4, 0, 0]} isAnimationActive={true} animationDuration={600}>
          {chartData.map((entry, index) => (
            <Cell key={index} fill={entry.color} fillOpacity={entry.isBase ? 0.85 : (entry.isPlanned ? 0.6 : 0.9)} />
          ))}
          <LabelList
            dataKey="value"
            position="top"
            content={(props) => renderBarLabel({ ...props, chartData })}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function PIEBITDABridgeTab({ portco, currentUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({ label: '', category: 'initiative', period: '', underwritten_value: '', actual_value: '' });
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});

  const canEdit = hasPI(currentUser, 'pi.bridge.edit');

  const loadItems = useCallback(() => {
    piApi.getBridgeItems(portco.id)
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [portco.id]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const startEditRow = (item) => {
    setEditingId(item.id);
    setEditRow({
      label: item.label || '',
      category: item.category || 'initiative',
      period: item.period || '',
      underwritten_value: item.underwritten_value ?? '',
      actual_value: item.actual_value ?? '',
    });
  };

  const handleSaveRow = async (itemId) => {
    await piApi.updateBridgeItem(portco.id, itemId, {
      label: editRow.label,
      category: editRow.category,
      period: editRow.period,
      underwritten_value: editRow.underwritten_value !== '' ? parseFloat(editRow.underwritten_value) : null,
      actual_value: editRow.actual_value !== '' ? parseFloat(editRow.actual_value) : null,
    }).catch(() => {});
    setEditingId(null);
    loadItems();
  };

  const handleDeleteRow = async (itemId) => {
    if (!window.confirm('Delete this bridge item?')) return;
    await piApi.deleteBridgeItem(portco.id, itemId).catch(() => {});
    loadItems();
  };

  const handleAddRow = async () => {
    if (!newRow.label) return;
    await piApi.createBridgeItem(portco.id, {
      ...newRow,
      underwritten_value: parseFloat(newRow.underwritten_value) || null,
      actual_value:       parseFloat(newRow.actual_value) || null,
    }).catch(() => {});
    setNewRow({ label: '', category: 'initiative', period: '', underwritten_value: '', actual_value: '' });
    setShowAddRow(false);
    loadItems();
  };

  const handleAnalyzeVariance = async () => {
    setAiLoading(true);
    try {
      const res = await piApi.analyzeEBITDAVariance(portco.id);
      setAiAnalysis(res.data.analysis);
    } catch (e) {
      console.error('[Bridge] AI error:', e);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>

        {/* LEFT: Bridge table */}
        <div style={{ flex: '0 0 42%' }}>
          <div className="pi-card">
            <div className="pi-card-header">
              <span className="pi-card-title">Bridge Items</span>
              {canEdit && (
                <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }}
                  onClick={() => setShowAddRow(s => !s)} aria-label="Add bridge item">
                  <Plus size={13} /> Add
                </button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                    {['Label', 'Category', 'Period', 'Underwritten', 'Actual', 'Variance', ...(canEdit ? [''] : [])].map(h => (
                      <th key={h || 'actions'} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const variance = (item.actual_value || 0) - (item.underwritten_value || 0);
                    const cellStyle = { padding: '9px 12px' };
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f8fafc', background: editingId === item.id ? '#fafbff' : 'transparent' }}>
                        <td style={{ ...cellStyle, color: '#334155', fontWeight: 500 }}>{item.label}</td>
                        <td style={cellStyle}>
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 8, background: '#f1f5f9', color: '#475569' }}>{item.category}</span>
                        </td>
                        <td style={{ ...cellStyle, color: '#64748b' }}>{item.period || '—'}</td>
                        <td style={{ ...cellStyle, color: '#64748b' }}>${item.underwritten_value || 0}M</td>
                        <td style={{ ...cellStyle, color: '#334155' }}>${item.actual_value || 0}M</td>
                        <td style={{ ...cellStyle, fontWeight: 600, color: variance >= 0 ? '#16a34a' : '#dc2626' }}>
                          {variance >= 0 ? '+' : ''}${variance.toFixed(1)}M
                        </td>
                        {canEdit && (
                          <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => startEditRow(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }} title="Edit">
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => handleDeleteRow(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2 }} title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Edit form panel */}
              {editingId && (() => {
                const formStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', width: '100%' };
                return (
                  <div style={{ padding: '14px 16px', borderTop: '2px solid #4f46e5', background: '#fafbff' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#4f46e5', marginBottom: 10 }}>Edit Bridge Item</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>LABEL *</label>
                        <input value={editRow.label} onChange={e => setEditRow(r => ({ ...r, label: e.target.value }))} style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>CATEGORY</label>
                        <select value={editRow.category} onChange={e => setEditRow(r => ({ ...r, category: e.target.value }))} style={formStyle}>
                          {['baseline','initiative','synergy','market','exit'].map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>PERIOD</label>
                        <input value={editRow.period} onChange={e => setEditRow(r => ({ ...r, period: e.target.value }))} placeholder="e.g. Q1 2025" style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>UNDERWRITTEN ($M)</label>
                        <input type="number" value={editRow.underwritten_value} onChange={e => setEditRow(r => ({ ...r, underwritten_value: e.target.value }))} style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>ACTUAL ($M)</label>
                        <input type="number" value={editRow.actual_value} onChange={e => setEditRow(r => ({ ...r, actual_value: e.target.value }))} style={formStyle} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="pi-btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
                      <button className="pi-btn-primary" onClick={() => handleSaveRow(editingId)}>Save Changes</button>
                    </div>
                  </div>
                );
              })()}
              {/* Add form panel */}
              {showAddRow && !editingId && (() => {
                const formStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', width: '100%' };
                return (
                  <div style={{ padding: '14px 16px', borderTop: '1px solid #f1f5f9', background: '#fafbff' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 10 }}>New Bridge Item</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>LABEL *</label>
                        <input value={newRow.label} onChange={e => setNewRow(r => ({ ...r, label: e.target.value }))} placeholder="Label" style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>CATEGORY</label>
                        <select value={newRow.category} onChange={e => setNewRow(r => ({ ...r, category: e.target.value }))} style={formStyle}>
                          {['baseline','initiative','synergy','market','exit'].map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>PERIOD</label>
                        <input value={newRow.period} onChange={e => setNewRow(r => ({ ...r, period: e.target.value }))} placeholder="e.g. Q1 2025" style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>UNDERWRITTEN ($M)</label>
                        <input type="number" value={newRow.underwritten_value} onChange={e => setNewRow(r => ({ ...r, underwritten_value: e.target.value }))} placeholder="e.g. 5.0" style={formStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>ACTUAL ($M)</label>
                        <input type="number" value={newRow.actual_value} onChange={e => setNewRow(r => ({ ...r, actual_value: e.target.value }))} placeholder="e.g. 6.0" style={formStyle} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="pi-btn-ghost" onClick={() => setShowAddRow(false)}>Cancel</button>
                      <button className="pi-btn-primary" onClick={handleAddRow}>Save Item</button>
                    </div>
                  </div>
                );
              })()}
              {!loading && items.length === 0 && !showAddRow && (
                <div className="pi-empty" style={{ padding: 24 }}>
                  <div className="pi-empty-title">No bridge items yet</div>
                  {canEdit && <button className="pi-btn-ghost" onClick={() => setShowAddRow(true)}><Plus size={13} /> Add first item</button>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Waterfall chart */}
        <div style={{ flex: 1 }}>
          <div className="pi-card" style={{ height: '100%' }}>
            <div className="pi-card-header">
              <span className="pi-card-title">EBITDA Waterfall</span>
              <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                {[['#2563eb','Baseline'],['#93c5fd','Planned'],['#16a34a','Positive'],['#dc2626','Negative']].map(([c, l]) => (
                  <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />{l}
                  </span>
                ))}
              </div>
            </div>
            <div className="pi-card-body">
              {items.length > 0 ? <WaterfallChart items={items} portco={portco} /> : (
                <div className="pi-empty" style={{ padding: 40 }}>
                  <div className="pi-empty-sub">Add bridge items to see the waterfall chart.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Variance Analysis */}
      <div className="pi-card">
        <div className="pi-card-header">
          <span className="pi-card-title">Variance Analysis</span>
          <button className="pi-ai-btn" onClick={handleAnalyzeVariance} disabled={aiLoading} aria-label="Analyze variance with AI">
            <Sparkles size={13} />
            {aiLoading ? 'Analyzing performance variance...' : 'Analyze Variance'}
          </button>
        </div>
        {aiLoading && (
          <div className="pi-loading" style={{ padding: '16px 18px' }}>
            <div className="pi-spinner" />
            <span>Analyzing performance variance...</span>
          </div>
        )}
        {aiAnalysis && (
          <div className="pi-card-body">
            <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.65, margin: 0 }}>{aiAnalysis}</p>
            <button className="pi-btn-ghost" style={{ marginTop: 12, fontSize: 12 }} onClick={() => { setAiAnalysis(null); handleAnalyzeVariance(); }}>Regenerate</button>
          </div>
        )}
        {!aiLoading && !aiAnalysis && (
          <div className="pi-card-body" style={{ color: '#94a3b8', fontSize: 13 }}>
            Click "Analyze Variance" to get an AI explanation of your top EBITDA variance drivers.
          </div>
        )}
      </div>
    </div>
  );
}
